import {createHtmlElement, getUrlParam} from '../../../functions';
import gsap from 'gsap';
import { isMobile, isSafari, projectMode, settingsData, threeJsVariables } from '../../../settings';
import { getDictionary, getLoaderData } from '../../crm/crm-requests';
import { setProjectSettings } from '../../threejs/autoSelectionBoxes';
import { intro } from '../../threejs/camera';
import { Loader } from '../../threejs/loader/Loader';
import { initProjectMainScript } from '../../threejs/autoProjectScript';
import { renderUpdateValuesOnDemand } from '../../../app';
import { BaseElement } from '../../interfaces/BaseElement';
import { DreamsSlider } from './DreamsSlider/Slider/DreamsSlider';
import { Button } from '../../interfaces/buttons/Button';
import { Fullscreen } from '../Fullscreen/Fullscreen';
import { changeLangToCurrent } from '../langSwitcher/langSwitcher';
import { SubPreloader } from './DreamsSlider/SubPreloader/SubPreloader';
import { Cloudflare } from '../../api/Cloudflare';
import {ProjectLogs} from "../ProjectLogs";
import { Cookie } from '../../Cookie/Cookie';
import { Sound } from './Sound';
import { BmbyMode } from '../../mode/BmbyMode';
import { DreamsPreloader } from './DreamsPreloader';
import { ViewMode } from '../../../common';


let dreamsSlider = null;

export function preloaderSliderInit() {
  document.addEventListener('gesturestart', function (e) {
    e.preventDefault();
  });

  const tl = gsap.timeline({repeat: -1, repeatDelay: 0.4});

  const {cdnRepository,projectFolder, features} = config;
  const url = Cloudflare.resize(`${cdnRepository}/${projectFolder}/resources/img/logo.png`,{
    width: 500
  });
  const mainContainer = new BaseElement()
      .addClass('preloader__main__container')
      .appendTo(document.body);
  new BaseElement()
      .addClass('preloader__top__container')
      .appendTo(mainContainer.DOMElement);
  new BaseElement()
      .addClass('preloader__bottom__container')
      .appendTo(mainContainer.DOMElement);
  const centerContainer = new BaseElement()
      .addClass('preloader__info__container')
      .appendTo(mainContainer.DOMElement);
  new BaseElement()
      .addClass('preloader__logo')
      .addStyle('background-image', `url(${url})`)
      .appendTo(centerContainer.DOMElement);
  new BaseElement()
      .addClass('preloader__progressbar')
      .appendTo(centerContainer.DOMElement);

  launchPreloaderSlider();
}

export function removePreloader () {
  const preloader = document.querySelector('.preloader__main__container');
  if (!preloader)
    return;
  const preloaderCenterBlock = preloader.querySelector('.preloader__info__container');
  const topBox = preloader.querySelector('.preloader__top__container');
  const bottomBox = preloader.querySelector('.preloader__bottom__container');

  preloader.classList.add('isFinish');

  return new Promise(resolve => {
    const tl = gsap.timeline();

    tl.to(preloaderCenterBlock,{ duration: 0.8, delay: 1, opacity:0}).addLabel('removeCenterBlockAnimation');
    tl.to(topBox,{duration: 1, ease: 'power3.inOut', height:0}, 'removeCenterBlockAnimation-=0.4');
    tl.to(bottomBox,{duration: 1, ease: 'power3.inOut', height:0},'removeCenterBlockAnimation-=0.4').then(()=>{
      preloader.remove();
      settingsData.preloaderRemoved = true;
      resolve();
    })

  });
}

let actualPercent = 0;
export function loaderLineProgress() {
  const percent = Loader.totalProgress();
  if (percent === 100) {
    renderUpdateValuesOnDemand(false);
    settingsData.slideShowed = true;
    return;
  }

  if (percent > actualPercent) {
    actualPercent = percent;
  }

  const progressBar = document.querySelector('.preloader__progressbar');
  if (progressBar){
    progressBar.style.width = `${actualPercent}%`;
  }
  requestAnimationFrame(loaderLineProgress);
}

async function launchPreloaderSlider() {
  let loaderData = null;
  let defaultLanguage = null;
  if (!BmbyMode.active) {
    ProjectLogs.timerStartFor('api_loader');
    const loaderResponse = await getLoaderData();
    loaderData = await loaderResponse.data;
    window.ld = loaderData;

    if (!Array.isArray(loaderData)) {
      skipSlider();
      return;
    }

    if (isMobile) {
      loaderData = loaderData.filter(slide => slide.type === 'mobile' || slide.type === 'all')
    } else {
      loaderData = loaderData.filter(slide => slide.type === 'web' || slide.type === 'all');
    }
    if (loaderData.length === 0) {
      skipSlider();
      return;
    }

    defaultLanguage = loaderData[0]?.defaultLanguage;
  } else {
    defaultLanguage = 'en';
  }

  const lang = getUrlParam('lang')?.toLowerCase() || Cookie.getCookie('language') || defaultLanguage;
  document.querySelector('html').setAttribute('lang', lang ? lang : 'en');

  await initProjectMainScript();

  const wrapper = new BaseElement()
      .addClass('dreams-slider')
      .addAttribute('id', 'preloader_slider')
      .appendTo(document.body)
      .DOMElement;

  if (BmbyMode.active) {
    wrapper.style.zIndex = 99999999999;
    wrapper.style.backgroundColor = 'var(--primaryBgNoAlpha)';
    const dreamsPreloader = new DreamsPreloader(wrapper).show();
    removePreloader();
    dreamsSlider = {
      callbacks: {
        createSkipBtn: () => {
          settingsData.slideShowed = true;
          intro();
          const awaitSearch = setInterval(() => {
            if (settingsData.activeViewMode === ViewMode.Cylinder) {
              clearInterval(awaitSearch);
              dreamsPreloader.remove();
              wrapper.remove();
            }
          },200);
        }
      }
    };
    return;
  }

  const canvas = new BaseElement('canvas')
      .appendTo(wrapper)
      .DOMElement;

  let skipPressed = false;
  const control = createControlPanel(wrapper);
  const subPreloader = new SubPreloader(control);
  let sound = null;
  const skipBtn = createSkipButton();
  skipBtn.event('click', () => {
    projectMode.LiteMode.iosGyroscopePermission();
    callbacks.removeSlider();
  });

  const callbacks = {
    launch: () => {
      removePreloader().then(() => {
        subPreloader.createSubTexts();
      })
    },
    repeat: () => {
      if (skipBtn.DOMElement.parentElement)
        callbacks.removeSlider();
    },
    removeSlider: () => {
      if (skipPressed)
        return;
      skipPressed = true;

      skipBtn.addClass('hidden');
      sound && sound.dispose();
      setTimeout(() => {
        settingsData.slideShowed = true;
        intro();
        dreamsSlider.dispose(0.1).then(() => wrapper.remove());
        if (projectMode.LiteMode.isActive())
          projectMode.LiteMode.resizeContainer();
      }, 200);
    },
    createSkipBtn: () => {
      subPreloader.remove();
      control.appendChild(skipBtn.DOMElement);
      sound = new Sound();
      sound.addButton(control,true,'sound-btn--preloader');
      threeJsVariables.slicer.setInitialIntroHeight();
    }
  };

  dreamsSlider = new DreamsSlider(canvas, {
    imageSize: isMobile ? 1024 : 2048,
    slideDuration: 4.5,
    forceAnimation: getUrlParam('force_animation') === '1',
    zoomDuration: 0.3,
    fadeDuration: 0.7,
    text: {
      settings: {
        font: 'https://dreamsimages.bmby.com/DreamsGeneralStorage/fonts/openSans/OpenSans-Bold.ttf',
        fontSize: isMobile ? 0.13 : 0.08,
        letterSpacing: -0.02,
        lineHeight: 1,
        outlineWidth: 0,
        outlineBlur: 0,
        outlineColor: '#2a2b2e',
        outlineOffsetX: 0.005,
        outlineOffsetY: 0.005,
        outlineOpacity: 0,
        maxWidth: 2.1,
        position: 0.001
      },
      animation: {
        textDuration: 0.8,
      }
    }
  }, callbacks);

  dreamsSlider.launch(loaderData);
}


function createControlPanel (parent) {
  const controlPanel = document.createElement('div');
  controlPanel.classList.add('control-panel');
  isSafari && controlPanel.classList.add('control-panel__safari-styles');
  parent.appendChild(controlPanel);
  return controlPanel;
}

function createSkipButton () {
    const skipBtn = new Button('Skip intro')
      .addClass('control-panel__button', 'preloader-skip-text');

    const fullscreenBtn = new Fullscreen().addBtn(skipBtn.DOMElement,'skip-btn');
    if (fullscreenBtn?.btn) {
      const lang = Cookie.getCookie('language') || 'en';
      skipBtn.DOMElement.classList.add('fullscreen-btn__skip-btn-parent');
      skipBtn.DOMElement.setAttribute('data-lang',lang);
    }

    return skipBtn;
}

export function getDreamsSliderScope () {
  return dreamsSlider;
}

function skipSlider() {
  initProjectMainScript();
  loaderLineProgress();
  settingsData.slideShowed = true;
}

